import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { API, resolveImg } from "@/App";
import SafeImg from "@/components/SafeImg";
import Seo from "@/components/Seo";

export default function Guests() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [guests, setGuests] = useState([]);
  const [years, setYears] = useState([]);
  const [activeYear, setActiveYear] = useState(searchParams.get("year") || "all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const y = searchParams.get("year");
    if (y) setActiveYear(y);
  }, [searchParams]);

  const changeYear = (y) => {
    setActiveYear(y);
    if (y === "all") setSearchParams({});
    else setSearchParams({ year: y });
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/guests`),
      axios.get(`${API}/guests/years`),
    ]).then(([g, y]) => {
      setGuests(g.data);
      setYears(y.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = guests;
    if (activeYear !== "all") list = list.filter((g) => g.year === Number(activeYear));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((g) => g.name.toLowerCase().includes(q) || g.role?.toLowerCase().includes(q));
    }
    return list;
  }, [guests, activeYear, query]);

  // Group by year for "all" view
  const grouped = useMemo(() => {
    if (activeYear !== "all") return null;
    const map = new Map();
    filtered.forEach((g) => {
      const y = g.year || 0;
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(g);
    });
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [filtered, activeYear]);

  return (
    <div data-testid="guests-page">
      <Seo
        title="Invitados de honor"
        description={`Archivo completo de los ${guests.length || 38}+ invitados de honor que han pasado por la mesa de la Tertulia In Dubio desde 2015: biografía, fecha de visita, apariciones en prensa. Pensamiento plural desde Sevilla.`}
        path="/invitados"
      />
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-2 md:pt-8 pb-8 md:pb-10">
        <span className="eyebrow"><span className="decor-line"></span>The Wall of Fame · 2015–2026</span>
        <div className="grid lg:grid-cols-[1.3fr_0.7fr] gap-10 items-end mt-4">
          <h1 className="font-serif text-[11vw] md:text-[80px] lg:text-[104px] leading-[0.9] tracking-tight font-light">
            Invitados<br/>de honor.
          </h1>
          <p className="prose-bio max-w-md">
            <strong>{guests.length} voces</strong> que han marcado nuestra mesa desde <strong>2015</strong>. Navega por años o busca por nombre. Pasa el cursor sobre cada retrato para conocer quién es; haz click para leer su biografía.
          </p>
        </div>
      </section>

      {/* Year tabs */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="border-y border-[var(--line)] py-5 flex flex-wrap items-center gap-5">
          <div className="flex flex-wrap gap-1 items-center" data-testid="year-tabs">
            <button
              onClick={() => changeYear("all")}
              className={`px-4 py-1.5 text-[11px] tracking-[0.22em] uppercase transition-colors border-b ${activeYear === "all" ? "border-[var(--text-ink)] text-[var(--text-ink)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-ink)]"}`}
              data-testid="year-all"
            >
              Todos <span className="text-[var(--gold-deep)] ml-1">{guests.length}</span>
            </button>
            {years.map((y) => (
              <button
                key={y.year}
                onClick={() => changeYear(String(y.year))}
                className={`px-4 py-1.5 text-[11px] tracking-[0.22em] uppercase transition-colors border-b ${String(y.year) === activeYear ? "border-[var(--text-ink)] text-[var(--text-ink)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-ink)]"}`}
                data-testid={`year-${y.year}`}
              >
                {y.year} <span className="text-[var(--gold-deep)] ml-1">{y.count}</span>
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Buscar invitado…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="ml-auto min-w-[200px] bg-transparent border-b border-[var(--line)] py-1 text-[13px] focus:outline-none focus:border-[var(--gold)] placeholder:text-[var(--text-muted)]"
            data-testid="guests-search"
          />
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="guests-grid">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-[rgba(18,18,18,0.05)] animate-pulse"></div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[var(--text-muted)]">
            <p className="font-serif text-3xl mb-2">Sin resultados</p>
            <p className="text-[13px]">Prueba con otro año o término.</p>
          </div>
        ) : grouped ? (
          <div className="space-y-20">
            {grouped.map(([year, items]) => (
              <div key={year} data-testid={`year-section-${year}`}>
                <div className="flex items-baseline gap-6 mb-6">
                  <h2 className="font-serif text-6xl md:text-7xl leading-none text-[var(--text-ink)]">
                    {year || "—"}
                  </h2>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    {items.length} {items.length === 1 ? "invitado" : "invitados"}
                  </span>
                  <div className="flex-1 h-px bg-[var(--line)]"></div>
                </div>
                <PortraitGrid items={items} hovered={hovered} setHovered={setHovered} />
              </div>
            ))}
          </div>
        ) : (
          <PortraitGrid items={filtered} hovered={hovered} setHovered={setHovered} />
        )}
      </section>
    </div>
  );
}

function PortraitGrid({ items, hovered, setHovered }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6">
      {items.map((g, i) => (
        <Link
          key={g.id}
          to={`/invitados/${g.slug}`}
          className="group block anim-fadeup"
          style={{ animationDelay: `${Math.min(i * 0.025, 0.4)}s` }}
          onMouseEnter={() => setHovered(g.id)}
          onMouseLeave={() => setHovered(null)}
          data-testid={`guest-card-${g.slug}`}
        >
          <div className="relative aspect-[3/4] overflow-hidden border border-[rgba(255,255,255,0.7)] shadow-[0_14px_40px_-20px_rgba(22,40,60,0.22)] transition-all duration-500 group-hover:shadow-[0_30px_70px_-25px_rgba(22,40,60,0.35)]">
            {/* Luminous border on hover */}
            <div className="absolute inset-0 z-10 pointer-events-none transition-opacity duration-500 opacity-0 group-hover:opacity-100"
              style={{
                boxShadow: "inset 0 0 0 1px rgba(201,167,74,0.7), 0 0 30px -6px rgba(152,197,220,0.35)",
              }}
            ></div>
            <SafeImg
              src={g.image}
              alt={g.name}
              name={g.name}
              className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.05]"
              style={{ filter: "saturate(0.88) contrast(1.04)" }}
            />
            {/* Overlay with bio preview */}
            <div className="absolute inset-0 z-20 flex flex-col justify-end p-4 bg-gradient-to-t from-[rgba(10,10,10,0.88)] via-[rgba(10,10,10,0.45)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400">
              <span className="text-[9.5px] uppercase tracking-[0.22em] text-[var(--gold)] mb-1.5">
                {g.year || "—"} · {g.visit_date?.replace(/^\d+\s+de\s+/, "").replace(/\s+de\s+\d{4}$/, "") || "Invitado"}
              </span>
              <h3 className="font-serif text-[22px] md:text-[26px] leading-[1.02] text-white mb-1.5">
                {g.name}
              </h3>
              <p className="text-[11px] text-[rgba(255,255,255,0.78)] line-clamp-2 font-light">
                {g.role}
              </p>
              <span className="mt-2 text-[9.5px] tracking-[0.22em] uppercase text-white/70 border-b border-white/30 pb-0.5 self-start">Ver ficha →</span>
            </div>
          </div>
          {/* Name always visible below */}
          <div className="mt-3 px-0.5">
            <h3 className="font-serif text-[18px] leading-tight text-[var(--text-ink)] group-hover:text-[var(--gold-deep)] transition-colors">
              {g.name}
            </h3>
            <p className="text-[10.5px] uppercase tracking-[0.16em] text-[var(--text-muted)] mt-1 line-clamp-1">
              {g.role || "Invitado"}
            </p>
          </div>
        </Link>
      ))}
    </div>
  );
}
